import type { IObservableArray } from 'mobx';

import type {
  Account,
  Billable,
  Event,
  FacebookCampaign,
  FacebookDisplayCreative,
  Goal,
  IFacebook,
  Segment,
  Targetable,
  Targeting,
  User,
} from '@feathr/blackbox';
import { flattenErrors } from '@feathr/hooks';

import type { ICampaignValidationErrors } from '../../CampaignSummary';
import type { IUniqueEmailsResult } from '../AdWizard/AddWizard.useReconcileUniqueEmails';
import { validateStepTargets } from '../AdWizard/AdWizardAudienceStep';
import { validateStepBudget } from '../AdWizard/AdWizardBudgetStep';
import { validateStepGoals } from '../StepGoals';
import { validateStepCreatives } from './FacebookCreativesStep';
import { validateStepGeneral } from './FacebookGeneralStep';

interface ICampaignDetails {
  campaign: FacebookCampaign;
  targetings: IObservableArray<Targeting>;
  targetingSegments?: Segment[];
  targetables?: Targetable[];
  goals: IObservableArray<Goal>;
  goalSegments?: Segment[];
  creatives: IObservableArray<FacebookDisplayCreative>;
  billable?: Billable;
  account: Account;
  event: Event;
  integration?: IFacebook;
  emailValidation: IUniqueEmailsResult;
}

export function validate(
  details: ICampaignDetails,
  isOnProduction: boolean,
  user?: User,
  noMaxBudget?: boolean,
): ICampaignValidationErrors {
  const { campaign, targetings, targetingSegments, targetables } = details;
  const one = validateStepGeneral(details.campaign);
  const two = flattenErrors(
    validateStepTargets({
      campaign,
      targetings,
      segments: targetingSegments ?? [],
      targetables: targetables ?? [],
      emailValidation: details.emailValidation,
    }),
  );
  const four = validateStepCreatives(details.campaign, details.creatives);
  const five = validateStepBudget({
    campaign: details.campaign,
    event: details.event,
    billable: details.billable,
    account: details.account,
    user,
    noMaxBudget,
    isOnProduction,
  });
  const six = validateStepGoals({ goals: details.goals, goalSegments: details.goalSegments });
  const facebookIntegrationErrors = details.account.validateFacebookIntegration();
  return {
    name: one,
    targets: two,
    creatives: four,
    duration: [...(five.date_end ?? []), ...(five.date_end ?? [])],
    budget: five.budget,
    goals: six.goals,
    facebook: facebookIntegrationErrors.map((e) => e.message),
  };
}
